import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import DescriptionIcon from "@mui/icons-material/Description";
import apiClient from "./utils/apiClient";
import { useInputDefinitionsStore } from "./store/inputDefinitionsStore";

const formatTs = (ms) => {
  if (!ms) return "";
  try { return new Date(ms).toLocaleString(); } catch { return ""; }
};

/**
 * Unified picker for "what should this Run use as its input?"
 *
 * Props:
 *   open, onClose          — dialog visibility
 *   userId                 — current user (for /input-list lookup)
 *   onPickDefinition(def)  — called when user selects a Library definition
 *   onPickFile(file)       — called when user selects an uploaded xlsx
 *   title                  — optional dialog title (default: "Browse Inputs")
 */
export default function InputBrowser({
  open,
  onClose,
  userId,
  onPickDefinition,
  onPickFile,
  title = "Browse Inputs",
}) {
  const { definitions, fetchDefinitions } = useInputDefinitionsStore();
  const [tab, setTab] = useState(0);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // {kind, item}

  useEffect(() => {
    if (!open) return;
    setErr("");
    setSelected(null);
    fetchDefinitions(true);
    if (userId) {
      setLoadingFiles(true);
      apiClient
        .get(`/input-list?user_id=${encodeURIComponent(userId)}`)
        .then((rows) => setFiles(Array.isArray(rows) ? rows : []))
        .catch((e) => setErr(e?.message || "Failed to load uploaded files"))
        .finally(() => setLoadingFiles(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const needle = search.trim().toLowerCase();
  const visibleDefs = (definitions || [])
    .filter((d) => d.status !== "archived")
    .filter((d) => {
      if (!needle) return true;
      return (
        (d.name || "").toLowerCase().includes(needle) ||
        (d.product || "").toLowerCase().includes(needle) ||
        (d.description || "").toLowerCase().includes(needle)
      );
    });
  const visibleFiles = (files || []).filter((f) => {
    if (!needle) return true;
    return (
      (f.description || "").toLowerCase().includes(needle) ||
      (Array.isArray(f.files) ? f.files.join(" ") : f.files || "")
        .toLowerCase()
        .includes(needle)
    );
  });

  const handleConfirm = () => {
    if (!selected) return;
    if (selected.kind === "definition" && onPickDefinition) {
      onPickDefinition(selected.item);
    } else if (selected.kind === "file" && onPickFile) {
      onPickFile(selected.item);
    }
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr("")}>{err}</Alert>}
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelected(null); }} sx={{ mb: 1 }}>
          <Tab
            icon={<LibraryBooksIcon fontSize="small" />}
            iconPosition="start"
            label={`Library (${visibleDefs.length})`}
          />
          <Tab
            icon={<DescriptionIcon fontSize="small" />}
            iconPosition="start"
            label={`Uploaded files (${visibleFiles.length})`}
          />
        </Tabs>

        <TextField
          size="small"
          fullWidth
          placeholder="Search name / product / description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />

        <Box sx={{ minHeight: 240, maxHeight: 360, overflowY: "auto" }}>
          {tab === 0 && (
            <List dense>
              {visibleDefs.length === 0 && (
                <Typography variant="body2" sx={{ color: "text.secondary", p: 2 }}>
                  No definitions match. Try the "Uploaded files" tab or clear the filter.
                </Typography>
              )}
              {visibleDefs.map((d) => {
                const isSel = selected?.kind === "definition" && selected.item.id === d.id;
                return (
                  <ListItem key={d.id} disablePadding>
                    <ListItemButton
                      selected={isSel}
                      onClick={() => setSelected({ kind: "definition", item: d })}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <strong>{d.name || "Untitled"}</strong>
                            <Chip size="small" label={d.status || "draft"} />
                            {d.product && <Chip size="small" label={d.product} variant="outlined" />}
                          </Box>
                        }
                        secondary={
                          [
                            d.description,
                            `Modified ${formatTs(d.modified)}`,
                          ].filter(Boolean).join(" · ")
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
          {tab === 1 && (
            <List dense>
              {loadingFiles && (
                <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              {!loadingFiles && visibleFiles.length === 0 && (
                <Typography variant="body2" sx={{ color: "text.secondary", p: 2 }}>
                  No uploaded files match.
                </Typography>
              )}
              {visibleFiles.map((f) => {
                const isSel = selected?.kind === "file" && selected.item.id === f.id;
                const filenames = Array.isArray(f.files) ? f.files.join(", ") : f.files;
                return (
                  <ListItem key={f.id} disablePadding>
                    <ListItemButton
                      selected={isSel}
                      onClick={() => setSelected({ kind: "file", item: f })}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <strong>{filenames || f.id}</strong>
                            {f.validation_status && (
                              <Chip
                                size="small"
                                label={f.validation_status}
                                color={f.validation_status === "ok" ? "success" : f.validation_status === "warn" ? "warning" : "error"}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          [
                            f.description,
                            f.upload_date ? `Uploaded ${formatTs(f.upload_date)}` : null,
                          ].filter(Boolean).join(" · ")
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selected}
          onClick={handleConfirm}
        >
          {selected?.kind === "definition" ? "Use in Run" : selected?.kind === "file" ? "Process this file" : "Select…"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
